import { memo, useCallback } from "react";
import { useWatch } from "react-hook-form";
import type { FilterRangeData } from "@/api/filter-ranges";
import { HistogramSlider } from "@/components/base/histogram-slider";
import { LabelWithTooltip } from "@/components/filters/shared";
import type { useFilters } from "@/providers/filter-provider";

// Fallback histogram data when API data is not available
const DEFAULT_RISK_HISTOGRAM_DATA = [0.5, 0.4, 0.3, 0.2, 0.15, 0.1, 0.08, 0.05, 0.03, 0.02, 0.01, 0.01];

type VideosAtRiskPercentFilterProps = {
  form: ReturnType<typeof useFilters>["form"];
  rangeData?: FilterRangeData;
};

export const VideosAtRiskPercentFilter = memo(({ form, rangeData }: VideosAtRiskPercentFilterProps) => {
  const rangeMin = 0;
  const rangeMax = 100;
  const histogramData = rangeData?.histogram ?? DEFAULT_RISK_HISTOGRAM_DATA;

  // Use useWatch for efficient subscriptions to specific fields
  const minVideosAtRiskPercent = useWatch({ control: form.control, name: "minVideosAtRiskPercent" });
  const maxVideosAtRiskPercent = useWatch({ control: form.control, name: "maxVideosAtRiskPercent" });

  const minValue = Number(minVideosAtRiskPercent) || rangeMin;
  const maxValue = Number(maxVideosAtRiskPercent) || rangeMax;

  const handleMinChange = useCallback(
    (value: number) =>
      form.setValue("minVideosAtRiskPercent", value === rangeMin ? undefined : String(value), {
        shouldDirty: true,
        shouldTouch: true,
      }),
    [form, rangeMin],
  );

  const handleMaxChange = useCallback(
    (value: number) =>
      form.setValue("maxVideosAtRiskPercent", value === rangeMax ? undefined : String(value), {
        shouldDirty: true,
        shouldTouch: true,
      }),
    [form, rangeMax],
  );

  return (
    <div className="flex flex-col gap-2">
      <LabelWithTooltip
        label="% of Creator's Videos at Risk"
        tooltip="Videos at Risk"
        description="Percentage of the creator's total videos that contain brand safety risks."
      />
      <HistogramSlider
        min={rangeMin}
        max={rangeMax}
        minValue={Math.min(minValue, maxValue)}
        maxValue={Math.max(maxValue, minValue)}
        onMinChange={handleMinChange}
        onMaxChange={handleMaxChange}
        format="percent"
        histogramData={histogramData}
      />
    </div>
  );
});
